import React, { useState, useEffect } from 'react';
import { pickupEventsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import './PickupEventsManagement.css';

function PickupEventsManagement() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    scheduled_datetime: '',
    location: 'Main Warehouse',
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const response = await pickupEventsAPI.getAll();
      setEvents(response.data);
    } catch (error) {
      console.error('Error loading pickup events:', error);
      alert('Failed to load pickup events');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingEvent) {
        await pickupEventsAPI.update(editingEvent.id, formData);
        alert('Pickup event updated successfully!');
      } else {
        await pickupEventsAPI.create(formData);
        alert('Pickup event created successfully!');
      }
      resetForm();
      loadEvents();
    } catch (error) {
      alert('Error saving pickup event: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      scheduled_datetime: event.scheduled_datetime.substring(0, 16), // Format for datetime-local input
      location: event.location,
    });
    setShowCreateForm(true);
  };

  const handleConfirm = async (eventId) => {
    try {
      await pickupEventsAPI.confirmAdmin(eventId);
      loadEvents();
    } catch (error) {
      alert('Error confirming event');
    }
  };

  const handleUnconfirm = async (eventId) => {
    try {
      await pickupEventsAPI.unconfirmAdmin(eventId);
      loadEvents();
    } catch (error) {
      alert('Error unconfirming event');
    }
  };

  const handleViewDetails = (eventId) => {
    navigate(`/admin/pickup-events/${eventId}`);
  };

  const resetForm = () => {
    setFormData({
      scheduled_datetime: '',
      location: 'Main Warehouse',
    });
    setEditingEvent(null);
    setShowCreateForm(false);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="page pickup-events-page">
      <div className="page-header">
        <h1 className="page-title">Pickup Events Management</h1>
        {!showCreateForm && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary"
          >
            Create New Pickup Event
          </button>
        )}
      </div>

      {showCreateForm && (
        <div className="card event-form-card">
          <h2>{editingEvent ? 'Edit Pickup Event' : 'Create New Pickup Event'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Scheduled Date & Time *</label>
              <input
                type="datetime-local"
                name="scheduled_datetime"
                value={formData.scheduled_datetime}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Location *</label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="e.g., Main Warehouse, Storage Facility A"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                {editingEvent ? 'Update Event' : 'Create Event'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="events-list card">
        <h2>Pickup Events</h2>
        {events.length === 0 ? (
          <p>No pickup events scheduled. Create one above!</p>
        ) : (
          <div className="events-grid">
            {events.map((event) => (
              <div key={event.id} className="event-card">
                <div className="event-header">
                  <div>
                    <h3>{formatDateTime(event.scheduled_datetime)}</h3>
                    <p className="event-location">{event.location}</p>
                  </div>
                  <div className="event-code">
                    <span className="code-label">Code:</span>
                    <span className="code-value">{event.pickup_code}</span>
                  </div>
                </div>

                <div className="event-stats">
                  <div className="stat-item">
                    <span className="stat-label">Status:</span>
                    <span className={`status-badge status-${event.status}`}>
                      {event.status}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Drivers:</span>
                    <span className="stat-value">
                      {event.attendances?.length || 0} attending
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Deliveries:</span>
                    <span className="stat-value">
                      {event.deliveries?.length || 0} assigned
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Messages:</span>
                    <span className="stat-value">
                      {event.messages?.length || 0}
                    </span>
                  </div>
                </div>

                <div className="event-products">
                  <strong>Products Summary:</strong>
                  {event.product_summary && event.product_summary.length > 0 ? (
                    <ul className="product-list">
                      {event.product_summary.map((product) => (
                        <li key={product.product_id}>
                          {product.name}: {product.quantity} bags
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-products">No deliveries assigned yet</p>
                  )}
                </div>

                <div className="event-attendances">
                  <strong>Driver Attendance:</strong>
                  {event.attendances && event.attendances.length > 0 ? (
                    <ul className="attendance-list">
                      {event.attendances.map((attendance) => (
                        <li key={attendance.id}>
                          {attendance.driver_name}
                          {attendance.confirmed && (
                            <span className="confirmed-badge">✓ Confirmed</span>
                          )}
                          {!attendance.confirmed && (
                            <span className="pending-badge">Pending</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="no-drivers">No drivers assigned</p>
                  )}
                </div>

                <div className="event-actions">
                  <button
                    onClick={() => handleViewDetails(event.id)}
                    className="btn btn-primary btn-sm"
                  >
                    View Details & Chat
                  </button>
                  <button
                    onClick={() => handleEdit(event)}
                    className="btn btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                  {event.admin_confirmed ? (
                    <button
                      onClick={() => handleUnconfirm(event.id)}
                      className="btn btn-warning btn-sm"
                    >
                      Unconfirm
                    </button>
                  ) : (
                    <button
                      onClick={() => handleConfirm(event.id)}
                      className="btn btn-success btn-sm"
                    >
                      Confirm Attendance
                    </button>
                  )}
                </div>

                {event.admin_confirmed && (
                  <div className="admin-confirmed-banner">
                    ✓ You have confirmed attendance
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PickupEventsManagement;
